import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SearchIcon from '@mui/icons-material/Search';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Button, ButtonBase, Checkbox, Chip, Collapse, FormControl, FormHelperText, InputAdornment, InputLabel, Link, MenuItem, Popover, Select, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReviewOrder from './ReviewOrder';
import TERMS_CONTENT from './termsContent';
import { ONLINE_CART_TOKENS } from './style';
import { PRIVACY_POLICY, TERMS_AND_CONDITIONS } from './dummyData';
import { isCompleteDob, isValidDob } from './validation';
import { useOnlineCart } from './OnlineCartContext';
import { DisplayNote } from './WarningBox';
import CheckIcon from '@mui/icons-material/Check';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';

const PHONE_NUMBER_FIELD_ID = 'phoneNumber';
const CALLS_CONSENT_FIELD_ID = 'consentOrderUpdates';

function formatUSPhone(value) {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) {
        return digits;
    }
    if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function stripBusinessEntitySuffix(value) {
    return String(value || '')
        .replace(/\bllc\b/gi, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
}

export function FieldError({ error, size }) {
    if (!error) {
        return null;
    }

    return (
        <FormHelperText
            error={error}
            sx={{
                mt: 1,
                fontSize: size === 'compact' ? '0.8125rem' : '0.875rem',
                lineHeight: 1.4,
                fontWeight: 500,
                color: ONLINE_CART_TOKENS.error,
                '&.Mui-error': {
                    color: ONLINE_CART_TOKENS.error,
                },
            }}
        >
            {error}
        </FormHelperText>
    );
}

function TextQuestionField({ field, value, onChange, error, autoFocus, onFieldChange, allAnswers, styles = { labelSx: {} } }) {
    const inputHeight = field.size === 'compact' ? 44 : 48;
    const inputType = field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text';

    const [note, setNote] = useState('')
    const timeout = useRef(null)

    const handleInputChange = (event) => {
        const rawValue = event.target.value || '';
        if (field.format === 'zip5') {
            onChange(rawValue.replace(/\D/g, '').slice(0, 5));
            return;
        }
        if (field.type === 'phone' || field.format === 'usPhone' || field.validation === 'usPhone') {
            onChange(formatUSPhone(rawValue));
            return;
        }
        onChange(rawValue);
    };

    const displayNote = (note)=>{
        setNote(note)
        clearTimeout(timeout.current)
        timeout.current = setTimeout(() => {
            setNote('')
        }, 5000);
    }

    const handleBlur = () => {
        if (field.id === 'businessName' || field.id === 'backupBusinessName') {
            const sanitizedBusinessName = stripBusinessEntitySuffix(value);
            if (sanitizedBusinessName !== String(value || '')) {
                onChange(sanitizedBusinessName);
                field.note && displayNote(field.note)
            }
        }

        const hasPhoneValue = String(value || '').replace(/\D/g, '').length > 0;
        const shouldAutoEnableCallsConsent =
            field.id === PHONE_NUMBER_FIELD_ID &&
            hasPhoneValue &&
            !allAnswers?.[CALLS_CONSENT_FIELD_ID];

        if (shouldAutoEnableCallsConsent && onFieldChange) {
            onFieldChange(CALLS_CONSENT_FIELD_ID, true);
        }
    };

    return (
        <Box>
            <Typography
                component="label"
                htmlFor={field.id}
                sx={{
                    display: 'block',
                    fontSize: '0.875rem',
                    lineHeight: 1.4,
                    fontWeight: 600,
                    color: ONLINE_CART_TOKENS.textPrimary,
                    mb: 1,
                    ...styles.labelSx,
                }}
            >
                {field.label}
                {field.optional ? (
                    <Typography component="span" sx={{ ml: 0.6, fontWeight: 400, color: ONLINE_CART_TOKENS.textPlaceholder, fontSize: '0.85rem' }}>
                        (optional)
                    </Typography>
                ) : null}
            </Typography>
            <Box sx={{ position: 'relative' }}>
                <TextField
                    id={field.id}
                    type={inputType}
                    value={value}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder={field.placeholder || ''}
                    fullWidth
                    autoFocus={Boolean(autoFocus)}
                    error={Boolean(error)}
                    InputProps={{
                        startAdornment: field.prefix ? (
                            <InputAdornment position="start">
                                <Typography sx={{ fontSize: '0.9rem', color: ONLINE_CART_TOKENS.textMutedSoft, fontWeight: 500 }}>{field.prefix}</Typography>
                            </InputAdornment>
                        ) : undefined,
                    }}
                    inputProps={{
                        inputMode: field.type === 'phone' || field.format === 'zip5' || field.format === 'usPhone' || field.validation === 'usPhone' ? 'numeric' : undefined,
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            height: inputHeight,
                            pr: field.suffix ? 8 : 1.5,
                            borderRadius: ONLINE_CART_TOKENS.radiusMd,
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: error ? ONLINE_CART_TOKENS.error : ONLINE_CART_TOKENS.border,
                                borderWidth: 1,
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: error ? ONLINE_CART_TOKENS.error : ONLINE_CART_TOKENS.borderHover,
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: error ? ONLINE_CART_TOKENS.error : ONLINE_CART_TOKENS.ring,
                                borderWidth: 2,
                            },
                        },
                        '& .MuiInputBase-input': {
                            fontSize: '1rem',
                            lineHeight: 1.45,
                            color: ONLINE_CART_TOKENS.textPrimary,
                            '&::placeholder': {
                                color: ONLINE_CART_TOKENS.textPlaceholder,
                                opacity: 1,
                            },
                        },
                    }}
                />
                {field.suffix ? (
                    <Typography
                        sx={{
                            position: 'absolute',
                            right: 14,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            color: ONLINE_CART_TOKENS.textPlaceholder,
                            pointerEvents: 'none',
                        }}
                    >
                        {field.suffix}
                    </Typography>
                ) : null}
            </Box>
            <FieldError error={error} size={field.size} />
            {note && <FormHelperText>{note}</FormHelperText>}
        </Box>
    );
}

function SelectQuestionField({ field, value, onChange, error }) {
    const inputHeight = field.size === 'compact' ? 44 : 48;
    const [anchorEl, setAnchorEl] = useState(null);
    const [search, setSearch] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const optionsListRef = useRef(null);
    const triggerRef = useRef(null);
    const listboxId = `${field.id}-listbox`;
    const open = Boolean(anchorEl);
    const normalizeOptionValue = (optionValue) => String(optionValue ?? '').trim().toLowerCase();
    const normalizedCurrentValue = normalizeOptionValue(value);
    const isOptionSelected = (option) => {
        const normalizedOptionValue = normalizeOptionValue(option?.value);
        const normalizedOptionLabel = normalizeOptionValue(option?.label);
        return normalizedOptionValue === normalizedCurrentValue || normalizedOptionLabel === normalizedCurrentValue;
    };
    const selectedOption = (field.options || []).find((option) => isOptionSelected(option));

    const filteredOptions = useMemo(() => {
        const allOptions = field.options || [];
        if (!search.trim()) {
            return allOptions;
        }
        return allOptions.filter((option) => option.label.toLowerCase().includes(search.toLowerCase()));
    }, [field.options, search]);

    const handleToggle = (event) => {
        setAnchorEl((current) => {
            const isOpening = !current;
            if (isOpening) {
                const selectedIndex = filteredOptions.findIndex((option) => isOptionSelected(option));
                setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : filteredOptions.length > 0 ? 0 : -1);
            }
            return current ? null : event.currentTarget;
        });
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSearch('');
        setHighlightedIndex(-1);
        if (triggerRef.current && typeof triggerRef.current.focus === 'function') {
            window.requestAnimationFrame(() => {
                triggerRef.current.focus();
            });
        }
    };

    const handleSelect = (selectedValue) => {
        onChange(selectedValue);
        handleClose();
    };

    useEffect(() => {
        if (!open || highlightedIndex < 0) {
            return;
        }
        const listElement = optionsListRef.current;
        if (!listElement) {
            return;
        }
        const highlightedOption = listElement.querySelector(`[data-option-index="${highlightedIndex}"]`);
        if (highlightedOption && typeof highlightedOption.scrollIntoView === 'function') {
            highlightedOption.scrollIntoView({ block: 'nearest' });
        }
    }, [highlightedIndex, open]);

    return (
        <Box>
            <Typography
                component="label"
                htmlFor={field.id}
                sx={{
                    display: 'block',
                    fontSize: '0.875rem',
                    lineHeight: 1.4,
                    fontWeight: 600,
                    color: ONLINE_CART_TOKENS.textPrimary,
                    mb: 1,
                }}
            >
                {field.label}
            </Typography>

            <ButtonBase
                id={field.id}
                ref={triggerRef}
                onClick={handleToggle}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                        event.preventDefault();
                        if (!open) {
                            handleToggle(event);
                        }
                    } else if (event.key === 'Escape' && open) {
                        event.preventDefault();
                        handleClose();
                    }
                }}
                role="combobox"
                aria-expanded={open}
                aria-controls={open ? listboxId : undefined}
                aria-haspopup="listbox"
                sx={{
                    width: '100%',
                    height: inputHeight,
                    px: 1.75,
                    borderRadius: ONLINE_CART_TOKENS.radiusMd,
                    border: '1px solid',
                    borderColor: error ? ONLINE_CART_TOKENS.error : open ? ONLINE_CART_TOKENS.ring : ONLINE_CART_TOKENS.border,
                    backgroundColor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textAlign: 'left',
                    transition: 'border-color 0.2s ease',
                    '&:hover': {
                        borderColor: error ? ONLINE_CART_TOKENS.error : open ? ONLINE_CART_TOKENS.ring : ONLINE_CART_TOKENS.borderHover,
                    },
                    '&:focus': {
                        borderColor: error ? ONLINE_CART_TOKENS.error : ONLINE_CART_TOKENS.ring,
                        borderWidth: '2px'
                    },
                    '&:focus-visible': {
                        outline: 'none',
                        borderColor: error ? ONLINE_CART_TOKENS.error : ONLINE_CART_TOKENS.ring,
                        borderWidth: '2px',
                        boxShadow: 'none',
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    {selectedOption ? <LocationOnOutlinedIcon sx={{ fontSize: 18, color: ONLINE_CART_TOKENS.primaryStrong, mr: 1 }} /> : null}
                    <Typography
                        sx={{
                            fontSize: '1rem',
                            color: selectedOption ? ONLINE_CART_TOKENS.textDark : ONLINE_CART_TOKENS.textPlaceholder,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {selectedOption ? selectedOption.label : field.placeholder || 'Select'}
                    </Typography>
                </Box>
                <KeyboardArrowDownIcon
                    sx={{
                        fontSize: 20,
                        color: ONLINE_CART_TOKENS.textPlaceholder,
                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                    }}
                />
            </ButtonBase>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        width: anchorEl?.clientWidth || 320,
                        borderRadius: 2,
                        border: `1px solid ${ONLINE_CART_TOKENS.borderSoft}`,
                        boxShadow: ONLINE_CART_TOKENS.popoverShadow,
                        overflow: 'hidden',
                    },
                }}
            >
                <Box sx={{ p: 1.25, borderBottom: `1px solid ${ONLINE_CART_TOKENS.borderSoft}` }}>
                    <TextField
                        value={search}
                        onChange={(event) => {
                            setSearch(event.target.value);
                            setHighlightedIndex(0);
                        }}
                        onKeyDown={(event) => {
                            if (!filteredOptions.length) {
                                return;
                            }
                            if (event.key === 'ArrowDown') {
                                event.preventDefault();
                                setHighlightedIndex((prev) => Math.min(filteredOptions.length - 1, prev + 1));
                            } else if (event.key === 'ArrowUp') {
                                event.preventDefault();
                                setHighlightedIndex((prev) => (prev <= 0 ? 0 : prev - 1));
                            } else if (event.key === 'Enter' && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
                                event.preventDefault();
                                handleSelect(filteredOptions[highlightedIndex].value);
                            } else if (event.key === 'Escape') {
                                event.preventDefault();
                                handleClose();
                            }
                        }}
                        placeholder="Search states..."
                        fullWidth
                        autoFocus
                        size="small"
                        inputProps={{
                            'aria-controls': listboxId,
                            'aria-activedescendant': highlightedIndex >= 0 ? `${field.id}-option-${highlightedIndex}` : undefined,
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 18, color: ONLINE_CART_TOKENS.textPlaceholder }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <Box ref={optionsListRef} id={listboxId} role="listbox" sx={{ maxHeight: 240, overflowY: 'auto', py: 0.5 }}>
                    {filteredOptions.map((option, index) => {
                        const isSelected = isOptionSelected(option);
                        const isHighlighted = highlightedIndex === index;
                        return (
                            <ButtonBase
                                key={option.value}
                                id={`${field.id}-option-${index}`}
                                data-option-index={index}
                                role="option"
                                aria-selected={isSelected}
                                onClick={() => handleSelect(option.value)}
                                onMouseEnter={() => setHighlightedIndex(index)}
                                sx={{
                                    width: '100%',
                                    px: 2,
                                    py: 1.2,
                                    justifyContent: 'flex-start',
                                    textAlign: 'left',
                                    borderRadius: 0,
                                    backgroundColor: isSelected || isHighlighted ? ONLINE_CART_TOKENS.primaryBgSoft : 'transparent',
                                    color: isSelected ? ONLINE_CART_TOKENS.primaryStrong : ONLINE_CART_TOKENS.textDark,
                                    fontWeight: isSelected ? 700 : 500,
                                    '&:hover': {
                                        backgroundColor: isSelected ? ONLINE_CART_TOKENS.primaryBgSoftHover : ONLINE_CART_TOKENS.surfaceHover,
                                    },
                                }}
                            >
                                {option.label}
                            </ButtonBase>
                        );
                    })}
                    {filteredOptions.length === 0 ? <Typography sx={{ px: 2, py: 2.2, textAlign: 'center', fontSize: '0.875rem', color: ONLINE_CART_TOKENS.textPlaceholder }}>No states found</Typography> : null}
                </Box>
            </Popover>

            <FieldError error={error} size={field.size} />
        </Box>
    );
}

function BasicSelectQuestionField({ field, value, onChange, error }) {
    const inputHeight = field.size === 'compact' ? 44 : 48;
    return (
        <Box>
            <Typography
                component="label"
                htmlFor={field.id}
                sx={{
                    display: 'block',
                    fontSize: '0.875rem',
                    lineHeight: 1.4,
                    fontWeight: 600,
                    color: ONLINE_CART_TOKENS.textPrimary,
                    mb: 1,
                }}
            >
                {field.label}
                {field.optional ? (
                    <Typography component="span" sx={{ ml: 0.6, fontWeight: 400, color: ONLINE_CART_TOKENS.textPlaceholder, fontSize: '0.85rem' }}>
                        (optional)
                    </Typography>
                ) : null}
            </Typography>

            <FormControl fullWidth error={Boolean(error)}>
                <Select
                    id={field.id}
                    displayEmpty
                    value={value || ''}
                    onChange={(event) => onChange(event.target.value)}
                    IconComponent={KeyboardArrowDownIcon}
                    MenuProps={{
                        disableScrollLock: true,
                        MenuListProps: {
                            autoFocusItem: false,
                        },
                        PaperProps: {
                            sx: {
                                maxHeight: 280,
                                WebkitOverflowScrolling: 'touch',
                                overscrollBehaviorY: 'contain',
                            },
                        },
                    }}
                    renderValue={(selectedValue) => {
                        if (!selectedValue) {
                            return <Typography sx={{ color: ONLINE_CART_TOKENS.textPlaceholder }}>{field.placeholder || 'Select'}</Typography>;
                        }
                        const selectedOption = field.options?.find((option) => option.value === selectedValue);
                        return selectedOption?.label || selectedValue;
                    }}
                    sx={{
                        height: inputHeight,
                        borderRadius: ONLINE_CART_TOKENS.radiusMd,
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        '& .MuiSelect-icon': {
                            color: error ? ONLINE_CART_TOKENS.error : ONLINE_CART_TOKENS.border,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: error ? ONLINE_CART_TOKENS.error : ONLINE_CART_TOKENS.border,
                            borderWidth: 1,
                        },
                        '&:hover .MuiSelect-icon': {
                            color: error ? ONLINE_CART_TOKENS.error : ONLINE_CART_TOKENS.borderHover,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: error ? ONLINE_CART_TOKENS.error : ONLINE_CART_TOKENS.borderHover,
                        },
                        '&.Mui-focused .MuiSelect-icon': {
                            color: error ? ONLINE_CART_TOKENS.error : ONLINE_CART_TOKENS.ring,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: error ? ONLINE_CART_TOKENS.error : ONLINE_CART_TOKENS.ring,
                            borderWidth: 2,
                        },
                    }}
                >
                    {(field.options || []).map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
                <FieldError error={error} size={field.size} />
            </FormControl>
        </Box>
    );
}

function CheckboxQuestionField({ field, value, onChange, error }) {
    const checkboxId = `checkbox-${field.id}`;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Checkbox
                    id={checkboxId}
                    checked={Boolean(value)}
                    onChange={(event) => onChange(event.target.checked)}
                    size="small"
                    sx={{
                        mt: '2px',
                        p: 0.5,
                        color: error ? ONLINE_CART_TOKENS.error : ONLINE_CART_TOKENS.borderMuted,
                        '&.Mui-checked': {
                            color: ONLINE_CART_TOKENS.primary,
                        },
                    }}
                />
                <Box sx={{ pt: 0.25 }}>
                    <Typography
                        component="label"
                        htmlFor={checkboxId}
                        sx={{
                            fontSize: '0.875rem',
                            lineHeight: 1.5,
                            color: ONLINE_CART_TOKENS.textBody,
                            cursor: 'pointer',
                            userSelect: 'none',
                        }}
                    >
                        {field.label}
                    </Typography>
                    {field.helperText ? (
                        <Typography sx={{ mt: 0.25, fontSize: '0.75rem', lineHeight: 1.6, color: ONLINE_CART_TOKENS.textMuted, pl: '0rem' }}>
                            {field.helperText}
                            {(field.helperLinks || []).map((link, index) => (
                                <Box component="span" key={link.href}>
                                    <Link
                                        href={link.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        underline="always"
                                        sx={{
                                    fontSize: '0.75rem',
                                    lineHeight: 1.6,
                                    color: ONLINE_CART_TOKENS.textInk,
                                            fontWeight: 400,
                                            textDecorationColor: ONLINE_CART_TOKENS.textInk,
                                            '&:visited': {
                                                color: ONLINE_CART_TOKENS.textInk,
                                                textDecorationColor: ONLINE_CART_TOKENS.textInk,
                                            },
                                            '&:hover': {
                                                color: ONLINE_CART_TOKENS.textInk,
                                                textDecorationColor: ONLINE_CART_TOKENS.textInk,
                                            },
                                        }}
                                    >
                                        {link.label}
                                    </Link>
                                    {index < field.helperLinks.length - 1 ? ' and ' : ''}
                                </Box>
                            ))}
                            {field.helperTextSuffix || ''}
                        </Typography>
                    ) : null}
                    <FieldError error={error} size={field.size}/>
                </Box>
            </Box>
        </Box>
    );
}

function ChoiceIndicator({ selected }) {
    return (
        <Box
            sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: selected ? ONLINE_CART_TOKENS.primary : ONLINE_CART_TOKENS.border,
                backgroundColor: selected ? ONLINE_CART_TOKENS.primary : 'transparent',
                display: 'grid',
                placeItems: 'center',
                mt: 0.25,
                flexShrink: 0,
            }}
        >
            {selected ? <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ONLINE_CART_TOKENS.surface }} /> : null}
        </Box>
    );
}

function OptionLeadingIcon({ type }) {
    if (type === 'zap') {
        return <BoltOutlinedIcon sx={{ fontSize: 18, color: ONLINE_CART_TOKENS.accentOrange }} />;
    }
    if (type === 'clock') {
        return <AccessTimeOutlinedIcon sx={{ fontSize: 18, color: ONLINE_CART_TOKENS.textMuted }} />;
    }
    return null;
}

function formatBirthday(dateOfBirth) {
    const month = String(dateOfBirth?.month || '').padStart(2, '0');
    const day = String(dateOfBirth?.day || '').padStart(2, '0');
    const year = String(dateOfBirth?.year || '');
    if (!month || !day || !year) {
        return '';
    }
    return `${month}/${day}/${year}`;
}

function StateRequirementDobField({ field, value, onChange, error, allAnswers, onFieldChange, refs }) {
    const { setFieldErrors, setField } = useOnlineCart();

    const selectedStateCode = allAnswers?.stateOfFormation?.stateCode || '';
    const requiredStateCodes = Array.isArray(field.requiredWhenStateCodes) ? field.requiredWhenStateCodes : [];
    const isRequiredForState = requiredStateCodes.includes(selectedStateCode);

    useEffect(()=>{
        if(!isRequiredForState){            
            onFieldChange('dateOfBirth', undefined);
            if(refs?.inputFieldsContainerRef?.current){
                refs.inputFieldsContainerRef.current.style.display = 'none';
            }
        }else{
            const hasDob = isCompleteDob(value);
            const isValidDOB = isValidDob(value);
            if(!(hasDob && isValidDOB)){
                const hasDob = isCompleteDob(allAnswers?.relayBankDateOfBirth);
                const isValidDOB = isValidDob(allAnswers?.relayBankDateOfBirth);
                if (hasDob && isValidDOB) {
                    setField('dateOfBirth', allAnswers?.relayBankDateOfBirth);
                    setFieldErrors({
                        dateOfBirth: ''
                    })
                }else{
                    setField('dateOfBirth', undefined);
                    setFieldErrors({
                        dateOfBirth: ''
                    })  
                }
            }
            if(refs?.inputFieldsContainerRef?.current){
                refs.inputFieldsContainerRef.current.style.display = 'block';
            }     
        }
    },[isRequiredForState])

    const onRelayBankDobFieldChange = (value)=>{
        onFieldChange('relayBankDateOfBirth', value)
        setFieldErrors({
            relayBank: ''
        })
    }

    if (!isRequiredForState) {
        return null;
    }

    const reasonByCode = field.requirementReasonByStateCode || {};
    const reason = reasonByCode[selectedStateCode] || 'This state requires date of birth on LLC formation documents.';

    return (
        <Box sx={{ mt: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.1, borderLeft: `3px solid ${ONLINE_CART_TOKENS.primary}`, borderRadius: 1.25, bgcolor: ONLINE_CART_TOKENS.primaryBgMuted, px: 1.5, py: 1.15, mb: 2 }}>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: ONLINE_CART_TOKENS.primary, mt: 0.2 }} />
                <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.45, color: ONLINE_CART_TOKENS.primaryStrong }}>{reason}</Typography>
            </Box>

            <RelayBankDobSection
                field={field}
                value={value || { month: '', day: '', year: '' }}
                onChange={onChange}
                helperText={field.helperText || 'Required for state filing.'}
                error={error}
                onFieldChange={onRelayBankDobFieldChange}
            />
            <FieldError error={error} />
        </Box>
    );
}

const isErrorRelatedToDOB = (error = '')=>{
    // Get Dob Errors
    const dayError = error?.toLowerCase().includes('day') || false;
    const monthError = error?.toLowerCase().includes('month') || false;
    const yearError = error?.toLowerCase().includes('year') || false;
    const dobError = error?.toLowerCase().includes('date of birth') || false;
    return {
        dayError,
        monthError,
        yearError,
        dobError
    }
}

function RelayBankDobSection({ field, value, onChange, helperText, error, onFieldChange }) {
    const dateOfBirth = value || { month: '', day: '', year: '' };
    const hasDob = isCompleteDob(dateOfBirth);
    const isValidDOB = isValidDob(dateOfBirth);
    const [showDobInputs, setShowDobInputs] = useState(() => !isCompleteDob(value));

    const handleDobChange = (patch) => {
        const next = {
            month: dateOfBirth?.month || '',
            day: dateOfBirth?.day || '',
            year: dateOfBirth?.year || '',
            ...patch,
        };
        onChange(next);
    };
    const handleDobBlur = () => {
        const hasDob = isCompleteDob(dateOfBirth);
        const isValidDOB = isValidDob(dateOfBirth);

        if(hasDob && isValidDOB){
            onFieldChange && onFieldChange(dateOfBirth)
        }
    };

    if (hasDob && isValidDOB && !showDobInputs && !Boolean(error)) {
        return (
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: ONLINE_CART_TOKENS.textHeadingAlt }}>{field?.optInDetails?.dobLabel || 'Date of Birth'}</Typography>
                    <Button
                        onClick={() => {
                            setShowDobInputs(true);
                        }}
                        sx={{ color: ONLINE_CART_TOKENS.primary, fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: 0.5, padding: '2px 4px' }}
                    >
                        <EditOutlinedIcon sx={{ fontSize: 14 }} />
                        Edit
                    </Button>
                </Box>
                <Box sx={{ px: 1.5, py: 1.25, borderRadius: 1.5, bgcolor: ONLINE_CART_TOKENS.neutralBgSoft, fontSize: '0.875rem', color: ONLINE_CART_TOKENS.textStrong }}>
                    {formatBirthday(dateOfBirth)}
                </Box>
                <Typography sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.75rem', color: ONLINE_CART_TOKENS.textMuted }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 14, color: ONLINE_CART_TOKENS.success }} />
                    Already provided for your LLC filing.
                </Typography>
            </Box>
        );
    }

    // Get Dob Errors
    const dayError = isErrorRelatedToDOB(error).dayError
    const monthError = isErrorRelatedToDOB(error).monthError
    const yearError = isErrorRelatedToDOB(error).yearError
    const dobError = isErrorRelatedToDOB(error).dobError
    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: ONLINE_CART_TOKENS.textHeadingAlt }}>{field?.optInDetails?.dobLabel || 'Date of Birth'}</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.78rem', color: ONLINE_CART_TOKENS.textMuted, mb: 1.2 }}>{helperText}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 1.25 }}>
                <FormControl size="small">
                    <InputLabel id={`${field.id}-dob-month`}>Month</InputLabel>
                    <Select
                        labelId={`${field.id}-dob-month`}
                        value={dateOfBirth?.month || ''}
                        label="Month"
                        onChange={(event) => handleDobChange({ month: event.target.value })}
                        SelectDisplayProps={{
                            ...(monthError || dobError  ? {'data-online-cart-dob-part': 'error'} : {})
                        }}
                        onBlur={handleDobBlur}
                        sx={{
                            height: 42,
                            borderRadius: 1.2,
                            '& .MuiSelect-icon': {
                                color: ONLINE_CART_TOKENS.borderMuted,
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: monthError || dobError ? (theme) => theme.palette.error.main : ONLINE_CART_TOKENS.border,
                            },
                            '&:hover .MuiSelect-icon': {
                                color: ONLINE_CART_TOKENS.borderHover,
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: monthError || dobError ? (theme) => theme.palette.error.main : ONLINE_CART_TOKENS.borderHover,
                            },
                            '&.Mui-focused .MuiSelect-icon': {
                                color: ONLINE_CART_TOKENS.ring,
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: monthError || dobError ? (theme) => theme.palette.error.main : ONLINE_CART_TOKENS.ring,
                                borderWidth: 2
                            },
                        }}
                    >
                        {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => (
                            <MenuItem key={month} value={String(month).padStart(2, '0')}>
                                {new Date(2000, month - 1).toLocaleString('en-US', { month: 'long' })}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    placeholder="DD"
                    value={dateOfBirth?.day || ''}
                    error={dayError || dobError}
                    onChange={(event) => handleDobChange({ day: event.target.value.replace(/\D/g, '').slice(0, 2) })}
                    onBlur={handleDobBlur}
                    inputProps={{
                        ...(dayError || dobError  ? {'data-online-cart-dob-part': 'error'} : {})
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { height: 42, borderRadius: 1.2 } }}
                />
                <TextField
                    size="small"
                    placeholder="YYYY"
                    value={dateOfBirth?.year || ''}
                    error={yearError || dobError}
                    onChange={(event) => handleDobChange({ year: event.target.value.replace(/\D/g, '').slice(0, 4) })}
                    onBlur={handleDobBlur}
                    inputProps={{
                        ...(yearError || dobError  ? {'data-online-cart-dob-part': 'error'} : {})
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { height: 42, borderRadius: 1.2 } }}
                />
            </Box>
        </Box>
    );
}

function ChoiceCardsField({ field, value, onChange, error, allAnswers, onFieldChange }) {
    const { setFieldErrors, setField} = useOnlineCart();

    // Start the "Why do I need this?" benefits panel open when the product is configured with
    // "Help open by default" (Help_Expanded_By_Default__c) so its benefits show without a click.
    const [showDetails, setShowDetails] = useState(Boolean(field.helpExpandedByDefault));
    const isRelayBankField = field.id === 'relayBank';
    const hasDetailsPanel = Boolean(field.optInDetails);
    const selectedValue = value && typeof value === 'object' ? value.selected : value;
    const details =
        hasDetailsPanel && isRelayBankField
            ? {
                  dateOfBirth: allAnswers?.relayBankDateOfBirth || { month: '', day: '', year: '' },
                  employeeCount: allAnswers?.relayEmployeeCount || field.optInDetails?.employeeOptions?.[0] || '',
              }
            : value && typeof value === 'object'
              ? value.details || {}
              : {};
    const showOptInPanel = hasDetailsPanel && selectedValue === field.optInDetails.showForValue;
    const hasDobError = isErrorRelatedToDOB(error).dayError || isErrorRelatedToDOB(error).monthError || isErrorRelatedToDOB(error).yearError || isErrorRelatedToDOB(error).dobError;

    const setSelectedValue = (nextSelectedValue) => {
        if (hasDetailsPanel && isRelayBankField) {
            onChange(nextSelectedValue);
            if (onFieldChange && nextSelectedValue === field?.optInDetails?.showForValue && !allAnswers?.relayEmployeeCount) {
                onFieldChange('relayEmployeeCount', field.optInDetails.employeeOptions?.[0] || '');
            }
            return
        }
        onChange(nextSelectedValue);
    };

    const setDetails = (nextDetails) => {
        if (isRelayBankField && onFieldChange) {
            if (nextDetails.relayBankDateOfBirth) {
                onFieldChange('relayBankDateOfBirth', nextDetails.relayBankDateOfBirth);
            }
            if (nextDetails.employeeCount) {
                onFieldChange('relayEmployeeCount', nextDetails.employeeCount);
            }
            return;
        }
    };

    useEffect(()=>{
        if(isRelayBankField && selectedValue !== field?.optInDetails?.showForValue){
            onFieldChange('relayBankDateOfBirth', undefined);
            onFieldChange('relayEmployeeCount', undefined);
        }else if(isRelayBankField && selectedValue === field?.optInDetails?.showForValue){
            const hasDob = isCompleteDob(allAnswers?.relayBankDateOfBirth);
            const isValidDOB = isValidDob(allAnswers?.relayBankDateOfBirth);
            if(!(hasDob && isValidDOB)){
                const hasDob = isCompleteDob(allAnswers?.dateOfBirth);
                const isValidDOB = isValidDob(allAnswers?.dateOfBirth);
                if (hasDob && isValidDOB) {
                    setField('relayBankDateOfBirth', allAnswers?.dateOfBirth);
                    setFieldErrors({
                        relayBank: ''
                    })
                }else{
                    setField('relayBankDateOfBirth', undefined);
                    setFieldErrors({
                        relayBank: ''
                    })  
                }
            }
        }
    },[isRelayBankField, selectedValue])

    return (
        <Box>
            {field.benefits && field.benefitsMode === 'always' ? (
                <Box sx={{ mt: 2.5, display: 'grid', rowGap: 2 }}>
                    {(field.benefits || []).map((item, index) => (
                        <Box key={`${item.text}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 1.5,
                                    bgcolor: ONLINE_CART_TOKENS.successBgSoft,
                                    display: 'grid',
                                    placeItems: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <BenefitIcon type={item.icon} />
                            </Box>
                            <Typography sx={{ fontSize: '1rem', color: ONLINE_CART_TOKENS.textStrong }}>{item.text}</Typography>
                        </Box>
                    ))}
                </Box>
            ) : null}

            <Box sx={{ display: 'grid', rowGap: 1.75 }}>
                {(field.options || []).map((option) => {
                    const isSelected = selectedValue === option.value;
                    return (
                        <>
                            <ButtonBase
                                key={option.value}
                                onClick={() => setSelectedValue(option.value)}
                                sx={{
                                    width: '100%',
                                    marginBottom: isRelayBankField ? -2 : 0,
                                    p: 2.5,
                                    borderRadius: 2,
                                    border: '2px solid',
                                    borderColor: isSelected ? ONLINE_CART_TOKENS.primary : ONLINE_CART_TOKENS.choiceBorder,
                                    backgroundColor: isSelected ? ONLINE_CART_TOKENS.primaryBgLight : isRelayBankField ? ONLINE_CART_TOKENS.surface : 'transparent',
                                    textAlign: 'left',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    gap: 2,
                                    transitionProperty: 'all',
                                    transitionTimingFunction: 'cubic-bezier(.4,0,.2,1)',
                                    transitionDuration: '.15s',
                                    boxShadow: isSelected ? `0 0 0 0 ${ONLINE_CART_TOKENS.surface}, 0 0 0 1px ${ONLINE_CART_TOKENS.primaryFocusRingSoft}` : 'none',
                                    '&:hover': {
                                        borderColor: isSelected ? ONLINE_CART_TOKENS.primary : ONLINE_CART_TOKENS.primaryBorderSoft,
                                    },
                                    '&:focus': {
                                        // outline: `2px solid ${ONLINE_CART_TOKENS.choiceBorder}`,
                                        // outlineOffset: '0px',
                                        borderColor: error ? ONLINE_CART_TOKENS.error : isSelected ? ONLINE_CART_TOKENS.primary : ONLINE_CART_TOKENS.primaryBorderSoft,
                                    },
                                    '&:focus-visible': {
                                        outline: `3px solid ${ONLINE_CART_TOKENS.ring}`,
                                        outlineOffset: '2px',
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <ChoiceIndicator selected={isSelected} />
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            {option.leadingIcon ? <OptionLeadingIcon type={option.leadingIcon} /> : null}
                                            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: ONLINE_CART_TOKENS.textHeadingAlt }}>{option.title}</Typography>
                                            {option.recommended ? (
                                                <Chip
                                                    label="Recommended"
                                                    size="small"
                                                    sx={{
                                                        height: 22,
                                                        borderRadius: '999px',
                                                        bgcolor: ONLINE_CART_TOKENS.badgeOrange,
                                                        color: ONLINE_CART_TOKENS.surface,
                                                        fontSize: '0.75rem',
                                                        fontWeight: 700,
                                                    }}
                                                />
                                            ) : null}
                                        </Box>
                                        <Typography sx={{ mt: 0.5, fontSize: '0.875rem', color: ONLINE_CART_TOKENS.textSupport }}>{option.subtitle}</Typography>
                                    </Box>
                                </Box>

                                <Typography
                                    sx={{
                                        fontSize: '1.125rem',
                                        fontWeight: 800,
                                        color: option.priceColor === 'success' ? ONLINE_CART_TOKENS.success : ONLINE_CART_TOKENS.textHeadingAlt,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {option.priceLabel}
                                </Typography>
                            </ButtonBase>
                            {isRelayBankField && (
                                <Collapse in={showOptInPanel && isSelected}>
                                    <Box
                                        sx={{
                                            mt: 2,
                                            mb: 1,
                                            p: 2.5,
                                            border: `1px solid ${ONLINE_CART_TOKENS.border}`,
                                            borderRadius: 2,
                                            backgroundColor: 'transparent',
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: ONLINE_CART_TOKENS.textMuted, mb: 3 }}>{field.optInDetails?.title}</Typography>

                                        <RelayBankDobSection
                                            field={field}
                                            value={details.relayBankDateOfBirth || allAnswers?.relayBankDateOfBirth || { month: '', day: '', year: '' }}
                                            error={hasDobError ? error : ''}
                                            onChange={(nextDob) => {
                                                setDetails({
                                                    relayBankDateOfBirth: nextDob,
                                                });
                                            }}
                                            onFieldChange={(value) => {
                                                onFieldChange('dateOfBirth', value);
                                                setFieldErrors({
                                                    dateOfBirth: '',
                                                });
                                            }}
                                            helperText={field.optInDetails?.dobHelperText || 'Required to verify your identity and open your business bank account.'}
                                        />
                                        {hasDobError && <FieldError error={error} />}

                                        <Typography sx={{ mt: 2, mb: 1, fontSize: '0.95rem', fontWeight: 700, color: ONLINE_CART_TOKENS.textHeadingAlt }}>
                                            {field.optInDetails?.employeeLabel}
                                        </Typography>
                                        <Select
                                            value={details.employeeCount || field.optInDetails?.employeeOptions?.[0] || ''}
                                            onChange={(event) => setDetails({ employeeCount: event.target.value })}
                                            sx={{
                                                width: '100%',
                                                height: 44,
                                                borderRadius: 1.2,
                                                '& .MuiSelect-icon': {
                                                    color: ONLINE_CART_TOKENS.borderMuted,
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: ONLINE_CART_TOKENS.border,
                                                },
                                                '&:hover .MuiSelect-icon': {
                                                    color: ONLINE_CART_TOKENS.borderHover,
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: ONLINE_CART_TOKENS.borderHover,
                                                },
                                                '&.Mui-focused .MuiSelect-icon': {
                                                    color: ONLINE_CART_TOKENS.ring,
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: ONLINE_CART_TOKENS.ring,
                                                },
                                            }}
                                        >
                                            {(field.optInDetails?.employeeOptions || []).map((opt) => (
                                                <MenuItem key={opt} value={opt}>
                                                    {opt}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <Typography sx={{ mt: 0.9, fontSize: '0.78rem', color: ONLINE_CART_TOKENS.textMuted }}>{field.optInDetails?.employeeHelperText}</Typography>
                                    </Box>
                                </Collapse>
                            )}
                        </>
                    );
                })}
            </Box>
            {!hasDobError && <FieldError error={error} />}

            {field.benefits && field.ctaLabel && (
                <Button
                    onClick={() => setShowDetails((prev) => !prev)}
                    sx={{
                        mt: 2.5,
                        color: ONLINE_CART_TOKENS.primary,
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        padding: 0,
                        // Cap at the card width and allow the label to wrap so a long help title
                        // breaks onto the next line instead of overflowing.
                        maxWidth: '100%',
                        textAlign: 'left',
                        whiteSpace: 'normal',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            color: 'inherit',
                            // minWidth:0 lets the flex item shrink below its content width so it wraps;
                            // overflowWrap breaks an unusually long unbroken token too.
                            minWidth: 0,
                            overflowWrap: 'anywhere',
                        }}
                    >
                        {field.ctaLabel || 'Why do I need this?'}
                    </Typography>
                    {showDetails ? <KeyboardArrowUpIcon sx={{ fontSize: 18, flexShrink: 0 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 18, flexShrink: 0 }} />}
                </Button>
            )}

            <Collapse in={showDetails}>
                <Box
                    sx={{
                        mt: 2,
                        border: `1px solid ${ONLINE_CART_TOKENS.border}`,
                        borderRadius: 2,
                        p: 2.5,
                        backgroundColor: 'transparent',
                        display: 'grid',
                        rowGap: 2.25,
                    }}
                >
                    {(field.benefits || []).map((item, index) => (
                        <Box key={`${item.text}-${index}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 1.5,
                                    bgcolor: ONLINE_CART_TOKENS.primaryBgMuted,
                                    display: 'grid',
                                    placeItems: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <BenefitIcon type={item.icon} />
                            </Box>
                            <Typography sx={{ fontSize: '1.02rem', color: ONLINE_CART_TOKENS.textSecondary, lineHeight: 1.55 }}>{item.text}</Typography>
                        </Box>
                    ))}
                </Box>
            </Collapse>

            {field.note ? <DisplayNote note={field.note} /> : null}

            {field.disclosure ? <Typography sx={{ mt: 3, fontSize: '0.82rem', lineHeight: 1.65, color: ONLINE_CART_TOKENS.textMuted }}>{field.disclosure}</Typography> : null}
        </Box>
    );
}

function TermsAcceptanceField({ field, value, onChange, error }) {
    const checkboxId = `terms-checkbox-${field.id}`;

    return (
        <Box>
            <Box
                sx={{
                    // mt: 3,
                    border: `1px solid ${ONLINE_CART_TOKENS.border}`,
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: ONLINE_CART_TOKENS.surface,
                    '&:focus-within': {
                        boxShadow: `0 0 0 3px ${ONLINE_CART_TOKENS.ring}`,
                    },
                }}
                
            >
                <Box
                    sx={{
                        maxHeight: 380,
                        overflowY: 'auto',
                        px: { xs: 2.25, sm: 2.75 },
                        borderRadius: 'inherit',
                        py: 2.5,
                        bgcolor: ONLINE_CART_TOKENS.panelBgOverlay,
                        '&:focus': {
                            outline: 'none',
                        },
                    }}
                    tabIndex={0}
                    role="region"
                    aria-label="Terms and conditions content"
                    data-content="true"
                >
                    <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.7, color: ONLINE_CART_TOKENS.textSupport, whiteSpace: 'pre-wrap', fontWeight: 500 }}>{TERMS_CONTENT}</Typography>
                </Box>
            </Box>

            <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox
                    id={checkboxId}
                    checked={Boolean(value)}
                    onChange={(event) => onChange(event.target.checked)}
                    size="small"
                    sx={{
                        mt: '2px',
                        p: 0.5,
                        color: error ? ONLINE_CART_TOKENS.error : ONLINE_CART_TOKENS.borderMuted,
                        '&.Mui-checked': {
                            color: ONLINE_CART_TOKENS.primary,
                        },
                    }}
                />
                <Typography
                    component="span"
                    sx={{
                        fontSize: '0.95rem',
                        lineHeight: 1.55,
                        color: ONLINE_CART_TOKENS.textStrong,
                        cursor: 'pointer',
                    }}
                >
                    <Box component="label" htmlFor={checkboxId} sx={{ cursor: 'pointer' }}>
                        I have read and agree to PRIME&apos;s{' '}
                    </Box>
                    <Link
                        href={TERMS_AND_CONDITIONS}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="always"
                        sx={{ fontSize: '0.95rem', color: ONLINE_CART_TOKENS.primary }}
                    >
                        Terms & Conditions
                    </Link>{' '}
                    and{' '}
                    <Link
                        href={PRIVACY_POLICY}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="always"
                        sx={{ fontSize: '0.95rem', color: ONLINE_CART_TOKENS.primary }}
                    >
                        Privacy Policy
                    </Link>
                    .
                </Typography>
            </Box>

            <FieldError error={error} />
        </Box>
    );
}

export function BenefitIcon({ type }) {
    const value = String(type || '').toLowerCase();
    const primarySx = { fontSize: 18, color: ONLINE_CART_TOKENS.primary };
    const successSx = { fontSize: 17, color: ONLINE_CART_TOKENS.textSuccess };

    switch (value) {
        case 'shield':
            return <ShieldOutlinedIcon sx={primarySx} />;
        case 'bolt':
        case 'zap':
            return <BoltOutlinedIcon sx={primarySx} />;
        case 'lock':
            return <LockOutlinedIcon sx={primarySx} />;
        case 'bell':
            return <NotificationsNoneOutlinedIcon sx={primarySx} />;
        case 'clock':
            return <AccessTimeOutlinedIcon sx={primarySx} />;
        case 'info':
            return <InfoOutlinedIcon sx={primarySx} />;
        case 'warning':
            return <WarningAmberRoundedIcon sx={primarySx} />;
        case 'cart':
            return <ShoppingCartOutlinedIcon sx={primarySx} />;
        case 'star':
            return <StarBorderRoundedIcon sx={primarySx} />;
        case 'wallet':
            return <PaymentsOutlinedIcon sx={successSx} />;
        case 'card':
            return <CreditCardOutlinedIcon sx={successSx} />;
        case 'trend':
            return <TrendingUpOutlinedIcon sx={successSx} />;
        case 'check':
        default:
            return <TaskAltOutlinedIcon sx={primarySx} />;
    }
}

export const VerifiedAddressCard = ({
    address,
    onUseVerifiedAddress,
    onKeepMine,
    loading,
    mobileView
}) => {
    if (!address?.shouldDisplayed) {
        return null;
    }

    return (
        <Box
            mt={2}
            sx={{
                p: '1rem',
                border: '1px solid',
                borderColor: 'primary.light',
                borderRadius: '1rem',
                backgroundColor: 'primary.50',
            }}
        >
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box
                    sx={{
                        width: {xs: '1.5rem', sm: mobileView ? '1.5rem' : '2rem'},
                        height: {xs: '1.5rem', sm: mobileView ? '1.5rem' : '2rem'},
                        borderRadius: '50%',
                        backgroundColor: 'primary.main',
                        color: 'common.white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <CheckIcon
                        sx={{
                            fontSize: {xs: '0.875rem', sm: mobileView ? '0.875rem' : '1rem'},
                        }}
                    />
                </Box>

                <Box flex={1} minWidth={0}>
                    <Typography
                        fontSize="0.875rem"
                        color="text.secondary"
                        lineHeight="143%"
                    >
                        We found a verified USPS address that slightly differs
                        from what you entered.
                    </Typography>

                    <Box
                        mt={1.25}
                        sx={{
                            p: '0.875rem',
                            borderRadius: '0.75rem',
                            backgroundColor: 'common.white',
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Typography
                            fontSize="0.938rem"
                            fontWeight={600}
                            lineHeight="143%"
                        >
                            {address?.addressLine1}
                        </Typography>

                        {address?.addressLine2 && (
                            <Typography
                                fontSize="0.938rem"
                                fontWeight={600}
                                lineHeight="143%"
                            >
                                {address?.addressLine2}
                            </Typography>
                        )}

                        <Typography
                            mt="0.125rem"
                            fontSize="0.938rem"
                            fontWeight={600}
                            lineHeight="143%"
                        >
                            {address?.city}, {address?.stateCode}{' '}
                            {address?.zipCode}
                        </Typography>
                    </Box>

                    <Stack
                        direction={{
                            xs: 'column',
                            sm: mobileView ? 'column' : 'row',
                        }}
                        spacing={1}
                        mt={1.25}
                        width="100%"
                    >
                        <Button
                            variant="contained"
                            onClick={onUseVerifiedAddress}
                            disabled={loading}
                            fullWidth
                            sx={{
                                height: '2.75rem',
                                borderRadius: '0.75rem',
                                fontSize: '0.875rem',
                                textTransform: 'none',
                                boxShadow: 'none',
                                px: '1rem',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Use Verified Address
                        </Button>

                        <Button
                            variant="outlined"
                            onClick={onKeepMine}
                            disabled={loading}
                            fullWidth
                            sx={{
                                height: '2.75rem',
                                borderRadius: '0.75rem',
                                fontSize: '0.875rem',
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Keep Mine
                        </Button>
                    </Stack>
                </Box>
            </Stack>
        </Box>
    );
};

export const UnverifiedAddressCard = ({ message, address, onProceed, loading, proceeded, mobileView }) => {
    if (!message) {
        return null;
    }

    return (
        <Box
            mt={2}
            sx={{
                p: '1rem',
                border: '1px solid',
                borderColor: ONLINE_CART_TOKENS.warningBorder,
                borderRadius: '1rem',
                backgroundColor: ONLINE_CART_TOKENS.warningBg,
            }}
        >
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box
                    sx={{
                        width: { xs: '1.5rem', sm: mobileView ? '1.5rem' : '2rem' },
                        height: { xs: '1.5rem', sm: mobileView ? '1.5rem' : '2rem' },
                        borderRadius: '50%',
                        backgroundColor: ONLINE_CART_TOKENS.warningAccent,
                        color: 'common.white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <PriorityHighRoundedIcon sx={{ fontSize: { xs: '0.875rem', sm: mobileView ? '0.875rem' : '1rem' } }} />
                </Box>

                <Box flex={1} minWidth={0}>
                    <Typography fontSize="0.875rem" color={ONLINE_CART_TOKENS.textSupport} lineHeight="143%">
                        {message}
                    </Typography>

                    {proceeded && (
                        <Typography mt={0.75} fontSize="0.875rem" fontWeight={600} color={ONLINE_CART_TOKENS.warningAccent} lineHeight="143%">
                            You have chosen to proceed with this address. Click Next to continue.
                        </Typography>
                    )}

                    {!proceeded && address?.addressLine1 && (
                        <Box
                            mt={1.25}
                            sx={{
                                p: '0.875rem',
                                borderRadius: '0.75rem',
                                backgroundColor: 'common.white',
                                border: '1px solid',
                                borderColor: ONLINE_CART_TOKENS.borderSoft,
                            }}
                        >
                            <Typography fontSize="0.938rem" fontWeight={600} lineHeight="143%">
                                {address?.addressLine1}
                            </Typography>

                            {address?.addressLine2 && (
                                <Typography fontSize="0.938rem" fontWeight={600} lineHeight="143%">
                                    {address?.addressLine2}
                                </Typography>
                            )}

                            <Typography mt="0.125rem" fontSize="0.938rem" fontWeight={600} lineHeight="143%">
                                {address?.city}, {address?.stateCode} {address?.zipCode}
                            </Typography>
                        </Box>
                    )}

                    {!proceeded && (
                        <Stack direction={{ xs: 'column', sm: mobileView ? 'column' : 'row' }} spacing={1} mt={1.25} width="100%">
                            <Button
                                variant="contained"
                                onClick={onProceed}
                                disabled={loading}
                                fullWidth
                                sx={{
                                    height: '2.75rem',
                                    borderRadius: '0.75rem',
                                    fontSize: '0.875rem',
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    px: '1rem',
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                Proceed with this address
                            </Button>
                        </Stack>
                    )}
                </Box>
            </Stack>
        </Box>
    );
};

export default function FieldRenderer({ field, value, onChange, error, onNext, autoFocus, onFieldChange, allAnswers, refs }) {
    if (field.type === 'text' || field.type === 'email' || field.type === 'phone') {
        return (
            <TextQuestionField
                field={field}
                value={value}
                onChange={onChange}
                error={error}
                autoFocus={autoFocus}
                onFieldChange={onFieldChange}
                allAnswers={allAnswers}
            />
        );
    }

    if (field.type === 'select') {
        return <SelectQuestionField field={field} value={value} onChange={onChange} error={error} />;
    }

    if (field.type === 'selectBasic') {
        return <BasicSelectQuestionField field={field} value={value} onChange={onChange} error={error} />;
    }

    if (field.type === 'checkbox') {
        return <CheckboxQuestionField field={field} value={value} onChange={onChange} error={error} />;
    }

    if (field.type === 'choiceCards') {
        return <ChoiceCardsField field={field} value={value || null} onChange={onChange} error={error} allAnswers={allAnswers} onFieldChange={onFieldChange} />;
    }

    if (field.type === 'dateOfBirth') {
        return <StateRequirementDobField field={field} value={value || { month: '', day: '', year: '' }} onChange={onChange} error={error} allAnswers={allAnswers} onFieldChange={onFieldChange} refs={refs}/>;
    }

    if (field.type === 'termsAcceptance') {
        return <TermsAcceptanceField field={field} value={value || false} onChange={onChange} error={error} />;
    }

    if (field.type === 'orderSummary') {
        return (
            <ReviewOrder
                value={value || {}}
                onChange={onChange}
                error={error}
                onPaymentProceed={onNext}
                onFieldChange={onFieldChange}
                allAnswers={allAnswers}
                TextQuestionField={TextQuestionField}
                BasicSelectQuestionField={BasicSelectQuestionField}
                FieldError={FieldError}
            />
        );
    }

    return null;
}
